if (document.documentElement.lang.split('-')[0] === 'hu') {
/* Card and PDP multi-shade order. Variant data is requested from the product's
   section endpoint after opening; each instance owns its own draft quantities. */
(function () {
  'use strict';

  function quantities(cart) {
    const result = new Map();
    for (const item of (cart && cart.items) || []) {
      const id = String(item.variant_id || item.id);
      result.set(id, (result.get(id) || 0) + Number(item.quantity || 0));
    }
    return result;
  }

  function reconcileCart(drafts, before, after) {
    const previous = quantities(before);
    const current = quantities(after);
    const remaining = new Map();
    const deltas = new Map();
    let added = 0;
    let requested = 0;
    for (const [id, qty] of drafts) {
      const wanted = Number(qty);
      const delta = Math.max(0, (current.get(String(id)) || 0) - (previous.get(String(id)) || 0));
      const done = Math.min(wanted, delta);
      requested += wanted;
      added += done;
      if (done) deltas.set(String(id), done);
      if (wanted > done) remaining.set(String(id), wanted - done);
    }
    return { remaining, deltas, added, requested, complete: added === requested };
  }

  function validQty(value, variant) {
    if (!Number.isSafeInteger(value) || value < 0) return false;
    if (value === 0) return true;
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    const limit = [variant.maxLeft, variant.stockLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
    return value >= min && (value - min) % step === 0 && value <= limit;
  }

  function maximumQty(variant) {
    const limit = [variant.maxLeft, variant.stockLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
    if (!Number.isFinite(limit)) return null;
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    return !variant.available || limit < min ? 0 : min + Math.floor((limit - min) / step) * step;
  }

  function nextQty(current, direction, variant) {
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    if (direction < 0) return current <= min ? 0 : current - step;
    return current === 0 ? min : current + step;
  }

  function saleFor(variant) {
    const current = Number(variant.price);
    const previous = Number(variant.compareAt);
    if (!Number.isSafeInteger(current) || !Number.isSafeInteger(previous) || current < 0 || previous <= current) return null;
    const percent = Math.round((previous - current) * 100 / previous);
    return { saving: previous - current, percent: percent > 0 ? percent : null };
  }

  function selectionTotals(drafts, variants, loaded, uncertain) {
    let count = 0, pieces = 0, current = 0, reference = 0;
    let valid = !!loaded && !uncertain;
    for (const [id, qty] of drafts) {
      if (qty === 0) continue;
      count += 1;
      pieces += qty;
      const variant = variants.get(String(id));
      const price = Number(variant && variant.price);
      if (!variant || !variant.available || !validQty(qty, variant) || !Number.isSafeInteger(price) || price < 0) { valid = false; continue; }
      const sale = saleFor(variant);
      current += price * qty;
      reference += (sale ? Number(variant.compareAt) : price) * qty;
      if (!Number.isSafeInteger(current) || !Number.isSafeInteger(reference)) valid = false;
    }
    const saving = valid && count ? reference - current : 0;
    const roundedPercent = saving > 0 && reference > 0 ? Math.round(saving * 100 / reference) : 0;
    return { count, pieces, current, reference, saving, percent: roundedPercent > 0 ? `${roundedPercent}%` : saving > 0 ? '<1%' : null, valid };
  }

  function usableSections(body) {
    const sections = body && body.sections;
    return sections && Object.values(sections).length && Object.values(sections).every((html) => typeof html === 'string' && html.length) ? sections : null;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { quantities, reconcileCart, validQty, maximumQty, nextQty, usableSections, saleFor, selectionTotals };
  if (typeof customElements === 'undefined' || customElements.get('pb-card-shades')) return;

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const money = (cents, currencyCodeEnabled = false) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'RON';
    if (currency === 'RON') return new Intl.NumberFormat('hu-HU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' lei';
    const helper = window.Alpine && Alpine.store && Alpine.store('xHelper');
    const formats = window.Eurus;
    const format = formats && (currencyCodeEnabled ? (formats.money_with_currency_format || formats.money_format) : (formats.money_format || formats.money_with_currency_format));
    if (helper && format) {
      const html = helper.formatMoney(cents, format);
      const span = document.createElement('span');
      span.innerHTML = html;
      return span.textContent.trim();
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'hu-HU', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'RON' }).format(cents / 100);
  };
  const normalize = (text) => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/(\d)\s*[-/,]\s*(\d)/g, '$1.$2');
  const shadeCollator = new Intl.Collator(document.documentElement.lang || 'ro', { numeric: true, sensitivity: 'base' });
  function compareShades(a, b) {
    const code = (variant) => {
      const label = String(variant.shade || variant.title || '').split('/').pop().trim();
      const match = label.match(/^(\d+)(?:\.(\d+))?(?=\s|$)/);
      if (!match) return null;
      const digits = match[1];
      const intense = digits !== '11' && digits.length > 1 && /^([1-9])\1+$/.test(digits);
      const reflect = match[2] || '';
      return { level: Number(intense ? digits[0] : digits), intensity: intense ? 1 : 0, reflectMain: Number(reflect[0] || 0), reflectSecondary: Number(reflect.slice(1) || 0) };
    };
    const left = code(a), right = code(b);
    if (left && right) return left.level - right.level || left.intensity - right.intensity || left.reflectMain - right.reflectMain || left.reflectSecondary - right.reflectSecondary || shadeCollator.compare(a.shade || a.title, b.shade || b.title);
    if (left) return -1;
    if (right) return 1;
    return shadeCollator.compare(a.shade || a.title, b.shade || b.title);
  }

  class CardShades extends HTMLElement {
    _shadeTitle(variant) {
      const index = Number(this.dataset.shadeOptionIndex ?? -1);
      return variant.shade || (index >= 0 && variant.options?.[index]) || variant.title;
    }

    _money(cents) { return money(cents, this.dataset.currencyCodeEnabled === 'true'); }

    connectedCallback() {
      this.isSizePicker = this.dataset.pickerKind === 'size';
      this.isPdp = this.dataset.mode === 'pdp';
      this.isSetFlow = this.isPdp && !!this.closest('.product-info')?.querySelector('pb-cro-set');
      this.trigger = this.querySelector('.pb-cro-card-shades__trigger');
      this.addButton = this.querySelector('.pb-cro-card-shades__add');
      if (this.isSetFlow) this.addButton.textContent = 'Tartsd meg az árnyalatokat';
      this.actions = this.querySelector('.pb-cro-card-shades__actions');
      this.savings = this.querySelector('.pb-cro-card-shades__savings');
      this.message = this.querySelector('.pb-cro-card-shades__message');
      this.variants = new Map();
      this.rows = new Map();
      this.drafts = new Map();
      this.committed = new Map();
      this.loaded = false;
      this.busy = false;
      this.uncertain = false;
      this.refreshQueued = false;
      if (this.isPdp) {
        this.sticky = this.querySelector('.pb-cro-shade-sticky');
        if (this.sticky) document.body.appendChild(this.sticky);
        this.stickyButton = this.sticky?.querySelector('.pb-cro-shade-sticky__add');
        this.stickyButton?.addEventListener('click', () => this.stickyHasSelection ? this.add() : this.open());
        this.sticky?.querySelector('.pb-cro-shade-sticky__edit').addEventListener('click', () => this.open());
        this._onStickyVisibility = (event) => {
          if (event.detail.section !== this.dataset.pdpSection) return;
          this.stickyVisible = event.detail.visible;
          if (this.sticky) this.sticky.hidden = !this.stickyVisible || !!this.panel;
        };
        window.addEventListener('pb-cro-sticky-visibility', this._onStickyVisibility);
        this.pdpRoot = this.closest('[data-pb-cro-pdp-multi]');
        this.technicalForm = this.pdpRoot?.querySelector(`#product-form-${this.dataset.pdpSection}`);
        this._guardTechnicalForm = (event) => { event.preventDefault(); event.stopImmediatePropagation(); };
        this.technicalForm?.addEventListener('submit', this._guardTechnicalForm, true);
        this._onViewedVariant = (event) => this._variantViewed(event);
        document.addEventListener(`eurus:product-page-variant-select:updated:${this.dataset.pdpSection}`, this._onViewedVariant);
      }
      this.trigger.addEventListener('click', () => this.open());
      this.addButton.addEventListener('click', () => this.add());
      this._onCartChanged = () => {
        if (this.busy) return;
        this.loaded = false;
        if (this.loadingPromise) this.refreshQueued = true;
        else if (this.panel) this._load();
        this._updateSummary();
      };
      this._onCartLineMutation = (event) => {
        if (!this.isPdp || event.action !== 'remove' || event.context !== 'cart') return;
        event.promise?.then(({ cart }) => {
          if (!cart) return;
          const removed = new Set((event.lines || []).map(line => String(line.id || line.merchandiseId || '').split(':')[0]));
          let cleared = false;
          for (const id of this.drafts.keys()) {
            if (removed.has(id)) { this.drafts.delete(id); cleared = true; }
          }
          for (const id of this.committed.keys()) {
            if (removed.has(id)) { this.committed.delete(id); cleared = true; }
          }
          if (cleared) {
            for (const row of this.rows.values()) row._syncQty();
            this._updateSummary();
          }
        }).catch(() => {});
      };
      if (this.isPdp) document.addEventListener('shopify:cart:lines-update', this._onCartLineMutation);
      document.addEventListener('eurus:cart:items-changed', this._onCartChanged);
      this._updateSummary();
    }

    disconnectedCallback() {
      this.close(false);
      document.removeEventListener('eurus:cart:items-changed', this._onCartChanged);
      if (this.isPdp) document.removeEventListener('shopify:cart:lines-update', this._onCartLineMutation);
      if (this.isPdp) {
        if (this.sticky) {
          const replacement = this.sticky.cloneNode(true);
          replacement.hidden = true;
          this.appendChild(replacement);
          this.sticky.remove();
        }
        window.removeEventListener('pb-cro-sticky-visibility', this._onStickyVisibility);
        this.technicalForm?.removeEventListener('submit', this._guardTechnicalForm, true);
        document.removeEventListener(`eurus:product-page-variant-select:updated:${this.dataset.pdpSection}`, this._onViewedVariant);
        clearTimeout(this.previewTimeout);
      }
    }

    _highlightViewed(scroll = false) {
      if (!this.isPdp) return;
      for (const [id, row] of this.rows) row.classList.toggle('pb-cro-card-shades__row--viewed', id === this.dataset.viewedVariantId);
      const viewed = this.rows.get(this.dataset.viewedVariantId);
      if (scroll && viewed && !viewed.hidden) viewed.scrollIntoView({ block: 'center' });
    }

    _variantViewed(event) {
      const data = event.detail?.html?.querySelector('.variant-selects-main [data-selected-variant]');
      if (!data) return;
      let variant;
      try { variant = JSON.parse(data.textContent); } catch (_) { return; }
      const id = variant?.id ? String(variant.id) : '';
      this.dataset.viewedVariantId = id;
      const title = this.querySelector('[data-pb-cro-viewed-label]');
      if (title) title.textContent = id ? this._shadeTitle(variant) : 'nincs kiválasztva';
      const context = this.querySelector('[data-pb-cro-viewed-context]');
      if (context) context.textContent = id
        ? 'A fenti ár és készlet erre a változatra vonatkozik. A kiválasztott termékek összege külön látható.'
        : 'Válaszd ki az árnyalatokat a pontos árak és a végösszeg megtekintéséhez.';
      this._highlightViewed();
      if (!this.previewPending) return;
      clearTimeout(this.previewTimeout);
      this.previewPending = false;
      this._syncPreviewButtons();
      if (id === this.previewTargetId) {
        this._setMessage(`Megjelenített árnyalat: ${this._shadeTitle(variant)}. A kiválasztott mennyiségek nem változtak.`);
        this._highlightViewed();
      } else this._setMessage('Az árnyalatot nem sikerült megjeleníteni. A kosárhoz kiválasztott termékek nem változtak.');
    }

    _syncPreviewButtons() {
      for (const row of this.rows.values()) {
        const button = row.querySelector('.pb-cro-card-shades__preview');
        if (button) button.disabled = !!this.previewPending;
      }
    }

    _previewVariant(variant) {
      if (!this.isPdp || this.previewPending) return;
      const picker = this.pdpRoot?.querySelector('.variant-selects-main');
      const selects = [...(picker?.querySelectorAll('select.product-form__input') || [])];
      const options = Array.isArray(variant.options) ? variant.options : [];
      const matches = selects.map((select, index) => [...select.options].find((option) => option.value === options[index] && option.dataset.optionValueId));
      // Check the entire combination before touching the technical picker.
      if (!selects.length || selects.length !== options.length || matches.some((option) => !option)) {
        this._setMessage('Ez a kombináció most nem jeleníthető meg. A kosárhoz kiválasztott termékek nem változtak.');
        return;
      }
      const previous = selects.map((select) => select.selectedIndex);
      this.previewPending = true;
      this.previewTargetId = String(variant.id);
      this._syncPreviewButtons();
      this._setMessage('Árnyalat megjelenítése…');
      selects.forEach((select, index) => {
        [...select.options].forEach((option) => option.toggleAttribute('selected', option === matches[index]));
        select.selectedIndex = [...select.options].indexOf(matches[index]);
      });
      selects[selects.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      this.previewTimeout = setTimeout(() => {
        if (!this.previewPending) return;
        this.previewPending = false;
        const current = this.pdpRoot?.querySelector('.variant-selects-main');
        if (current === picker) selects.forEach((select, index) => {
          [...select.options].forEach((option, optionIndex) => { option.selected = optionIndex === previous[index]; option.toggleAttribute('selected', optionIndex === previous[index]); });
        });
        this._syncPreviewButtons();
        this._setMessage('Az árnyalat frissítése nem sikerült. Próbáld újra. A kosárhoz kiválasztott termékek nem változtak.');
      }, 12000);
    }

    _setMessage(text) {
      this.message.textContent = text || '';
      if (this.panelMessage) this.panelMessage.textContent = text || '';
    }

    _updateSummary() {
      const { count, pieces, current, reference, saving, percent, valid } = selectionTotals(this.drafts, this.variants, this.loaded, this.uncertain);
      const label = this.isSizePicker ? (count ? `${count} ${this.dataset.pickerNoun || "változat"} · ${pieces} db` : (this.dataset.pickerLabel || 'Válassz változatot')) : (count ? `${count} árnyalat · ${pieces} db` : 'Válaszd ki az árnyalatokat');
      this.trigger.firstChild.textContent = `${label} `;
      const total = this.actions.querySelector('.pb-cro-card-shades__total');
      total.removeAttribute('aria-label');
      if (!count || !valid) total.textContent = `Összesen: ${count ? 'frissítés alatt' : '—'}`;
      else if (saving > 0) {
        const before = document.createElement('s');
        before.className = 'pb-cro-card-shades__reference';
        before.textContent = this._money(reference);
        const now = document.createElement('strong');
        now.className = 'pb-cro-card-shades__current-total';
        now.textContent = this._money(current);
        total.replaceChildren(document.createTextNode('Összesen: '), before, document.createTextNode(' '), now);
        total.setAttribute('aria-label', `Kedvezmény előtti összeg ${before.textContent}. Fizetendő összeg ${now.textContent}.`);
      } else total.textContent = `Összesen: ${this._money(current)}`;
      if (this.savings) {
        this.savings.hidden = !valid || !count || saving <= 0;
        this.savings.textContent = this.savings.hidden ? '' : `Megtakarítás: ${this._money(saving)} összesen (${percent})`;
      }
      this.addButton.disabled = !valid || !count || this.busy || this.uncertain;
      if (this.sticky) {
        this.stickyHasSelection = count > 0;
        this.sticky.querySelector('[data-pb-sticky-total]').hidden = !count;
        this.sticky.querySelector('[data-pb-sticky-total]').replaceChildren(...Array.from(total.childNodes, node => node.cloneNode(true)));
        this.stickyButton.textContent = count ? (this.isSetFlow ? 'Folytatás' : 'Kosárba') : 'Válaszd ki az árnyalatokat';
        this.stickyButton.disabled = this.busy || (count > 0 && this.addButton.disabled);
        this.sticky.hidden = !this.stickyVisible || !!this.panel;
      }
      for (const row of this.rows.values()) row._syncQty();
      if (this.isPdp) this.dispatchEvent(new CustomEvent('pb-cro:selection', { bubbles: true }));
    }

    _moveActions() {
      if (!this.panel) return;
      if (this.actions.parentElement !== this.footer) this.footer.appendChild(this.actions);
    }

    _position() {
      if (!this.panel) return;
      if (window.matchMedia('(max-width: 639px)').matches) {
        // iOS keyboards resize the visual viewport, not necessarily the layout viewport.
        const viewport = window.visualViewport;
        const width = viewport?.width || window.innerWidth;
        const availableHeight = viewport?.height || window.innerHeight;
        const height = Math.min(760, window.innerHeight * .86, availableHeight);
        this.panel.style.setProperty('--pb-panel-left', `${viewport?.offsetLeft || 0}px`);
        this.panel.style.setProperty('--pb-panel-top', `${(viewport?.offsetTop || 0) + availableHeight - height}px`);
        this.panel.style.setProperty('--pb-panel-width', `${width}px`);
        this.panel.style.setProperty('--pb-panel-height', `${height}px`);
        return;
      }
      this.panel.style.removeProperty('--pb-panel-width');
      this.panel.style.removeProperty('--pb-panel-height');
      const rect = this.trigger.getBoundingClientRect();
      const width = Math.min(460, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const height = Math.min(640, window.innerHeight - 24);
      const top = Math.max(12, Math.min(rect.bottom + 6, window.innerHeight - height - 12));
      this.panel.style.setProperty('--pb-panel-left', `${left}px`);
      this.panel.style.setProperty('--pb-panel-top', `${top}px`);
    }

    open() {
      if (this.panel) return;
      document.dispatchEvent(new CustomEvent('pb-cro:card-shades:open', { detail: this }));
      this.analyticsLocation = this.isPdp ? (this.sticky?.contains(document.activeElement) ? 'sticky' : 'pdp') : 'card';
      this.returnFocus = this.isPdp && !this.sticky?.contains(document.activeElement) ? this.trigger : document.activeElement;
      this.overlay = document.createElement('div');
      this.overlay.className = 'pb-cro-card-shades__overlay';
      this.overlay.addEventListener('click', () => this.close());
      this.panel = document.createElement('div');
      this.panel.className = 'pb-cro-card-shades__panel';
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.tabIndex = -1;
      this.panel.setAttribute('aria-label', `Válaszd ki az árnyalatokat pentru ${this.dataset.productTitle || 'produs'}`);
      this.panel.innerHTML = '<div class="pb-cro-card-shades__head"><strong></strong><button type="button" class="pb-cro-card-shades__close" aria-label="Bezárás">×</button></div><input class="pb-cro-card-shades__search" type="search" placeholder="Keresés kód vagy név alapján" aria-label="Keresés kód vagy név alapján"><p class="pb-cro-card-shades__load" role="status"></p><div class="pb-cro-card-shades__list"></div><div class="pb-cro-card-shades__footer"><p class="pb-cro-card-shades__footer-note" role="status"></p></div>';
      this.panel.querySelector('.pb-cro-card-shades__head strong').textContent = this.dataset.productTitle || 'Árnyalatok';
      this.panel.querySelector('.pb-cro-card-shades__close').addEventListener('click', () => this.close());
      this.search = this.panel.querySelector('.pb-cro-card-shades__search');
      if (this.isSizePicker) {
        const hu = document.documentElement.lang.split('-')[0] === 'hu';
        const label = this.dataset.pickerLabel || (hu ? 'Válassz változatot' : 'Selectează varianta');
        this.panel.setAttribute('aria-label', label + ': ' + this.dataset.productTitle);
        this.search.placeholder = this.dataset.pickerSearch || (hu ? 'Változat keresése' : 'Caută varianta');
        this.search.setAttribute('aria-label', this.search.placeholder);
      }
      this.search.addEventListener('input', () => this._filter());
      this.list = this.panel.querySelector('.pb-cro-card-shades__list');
      this.loadStatus = this.panel.querySelector('.pb-cro-card-shades__load');
      this.footer = this.panel.querySelector('.pb-cro-card-shades__footer');
      this.panelMessage = this.panel.querySelector('.pb-cro-card-shades__footer-note');
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(); }
        if (event.key !== 'Tab') return;
        const targets = [
          this.panel.querySelector('.pb-cro-card-shades__close'),
          this.search,
          ...this.list.querySelectorAll('button,input'),
          this.addButton
        ].filter((el) => el && !el.disabled && !el.closest('[hidden]'));
        if (!targets.length) return;
        if (event.shiftKey && document.activeElement === targets[0]) { event.preventDefault(); targets[targets.length - 1].focus(); }
        else if (!event.shiftKey && document.activeElement === targets[targets.length - 1]) { event.preventDefault(); targets[0].focus(); }
      });
      document.body.append(this.overlay, this.panel);
      this.trigger.setAttribute('aria-expanded', 'true');
      this._moveActions();
      this._position();
      this._onResize = () => { this._moveActions(); this._position(); };
      window.addEventListener('resize', this._onResize);
      window.visualViewport?.addEventListener('resize', this._onResize);
      window.visualViewport?.addEventListener('scroll', this._onResize);
      this._onOtherOpen = (event) => { if (event.detail !== this) this.close(false); };
      document.addEventListener('pb-cro:card-shades:open', this._onOtherOpen);
      this._updateSummary();
      // Opening the mobile picker must not open the keyboard or trigger Safari zoom.
      (window.matchMedia('(max-width: 639px)').matches ? this.panel : this.search).focus({ preventScroll: true });
      if (!this.loaded && !this.loadingPromise) this._load();
      else { this._renderAll(); this._highlightViewed(true); }
    }

    close(restoreFocus = true) {
      if (!this.panel) return;
      this.insertBefore(this.actions, this.message);
      this.overlay.remove();
      this.panel.remove();
      this.panel = null;
      this.rows.clear();
      this.panelMessage = null;
      this.trigger.setAttribute('aria-expanded', 'false');
      window.removeEventListener('resize', this._onResize);
      window.visualViewport?.removeEventListener('resize', this._onResize);
      window.visualViewport?.removeEventListener('scroll', this._onResize);
      document.removeEventListener('pb-cro:card-shades:open', this._onOtherOpen);
      this._updateSummary();
      if (restoreFocus && this.isConnected) (this.returnFocus || this.trigger).focus({ preventScroll: true });
    }

    async _load() {
      this.loaded = false;
      this.variants.clear();
      this.rows.clear();
      this.list.replaceChildren();
      this.loadStatus.textContent = (this.isSizePicker ? 'Változatok betöltése…' : 'Árnyalatok betöltése…');
      this.loadingPromise = (async () => {
        let next = this.dataset.productUrl;
        const seen = new Set();
        while (next) {
          const url = new URL(next, location.origin);
          url.searchParams.set('section_id', 'pb-cro-card-shades');
          const preview = new URLSearchParams(location.search).get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          if (seen.has(url.href)) throw new Error('Repeated variant page');
          seen.add(url.href);
          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Variant page unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = html.querySelector('[data-pb-card-page]');
          const data = page && page.querySelector('[data-pb-card-variants]');
          if (!data) throw new Error('Variant data missing');
          const variants = JSON.parse(data.textContent);
          for (const variant of variants) this.variants.set(String(variant.id), variant);
          if (this.panel) { this._renderAll(); this.loadStatus.textContent = (this.isSizePicker ? 'Az összes változat betöltése…' : 'Az összes árnyalat betöltése…'); }
          next = page.dataset.nextUrl || '';
        }
        this.loaded = true;
        if (this.panel) { this.loadStatus.textContent = ''; this._renderAll(); this._highlightViewed(true); }
      })().catch(() => {
        this.loaded = false;
        if (this.panel) this.loadStatus.textContent = (this.isSizePicker ? 'A változatokat nem sikerült betölteni. Zárd be, majd próbáld újra.' : 'Az árnyalatokat nem sikerült betölteni. Zárd be, majd próbáld újra.');
      }).finally(() => {
        this.loadingPromise = null;
        this._updateSummary();
        if (this.refreshQueued) {
          this.refreshQueued = false;
          this.loaded = false;
          if (this.panel) this._load();
        }
      });
      return this.loadingPromise;
    }

    _renderAll() {
      if (!this.panel) return;
      const orderedVariants = [...this.variants];
      if (!this.isSizePicker) orderedVariants.sort((a, b) => compareShades(a[1], b[1]));
      for (const [id, variant] of orderedVariants) {
        if (!this.rows.has(id)) {
          const row = this._row(variant);
          this.rows.set(id, row);
          this.list.appendChild(row);
        }
      }
      this._filter();
      this._highlightViewed();
      this._updateSummary();
    }

    _row(variant) {
      const id = String(variant.id);
      const row = document.createElement('div');
      row.className = 'pb-cro-card-shades__row';
      row.dataset.search = normalize([variant.sku, variant.shade, variant.title, ...(variant.options || [])].join(' '));
      const swatch = document.createElement('span');
      swatch.className = 'pb-cro-card-shades__swatch';
      if (variant.swatchImage) {
        const img = document.createElement('img');
        img.src = variant.swatchImage;
        img.alt = '';
        img.width = 36;
        img.height = 36;
        img.loading = 'lazy';
        swatch.appendChild(img);
      } else if (/^\d{1,3},\s*\d{1,3},\s*\d{1,3}$/.test(variant.swatchRgb || '')) {
        swatch.style.backgroundColor = `rgb(${variant.swatchRgb})`;
      } else swatch.classList.add('pb-cro-card-shades__swatch--empty');
      const details = document.createElement('div');
      details.className = 'pb-cro-card-shades__details';
      const title = document.createElement('span');
      title.className = 'pb-cro-card-shades__name';
      title.textContent = this._shadeTitle(variant);
      details.appendChild(title);
      if (variant.sku) {
        const code = document.createElement('span');
        code.className = 'pb-cro-card-shades__sku';
        code.textContent = `Kód: ${variant.sku}`;
        details.appendChild(code);
      }
      const prices = document.createElement('span');
      prices.className = 'pb-cro-card-shades__prices';
      const sale = saleFor(variant);
      if (sale) {
        const compare = document.createElement('s');
        compare.className = 'pb-cro-card-shades__compare';
        compare.textContent = variant.compareAtText || this._money(variant.compareAt);
        prices.appendChild(compare);
      }
      const price = document.createElement('span');
      price.className = sale ? 'pb-cro-card-shades__current--sale' : '';
      price.textContent = variant.priceText || this._money(variant.price);
      prices.appendChild(price);
      if (sale && sale.percent) {
        const percent = document.createElement('span');
        percent.className = 'pb-cro-card-shades__discount';
        percent.textContent = `−${sale.percent}%`;
        prices.appendChild(percent);
      }
      details.appendChild(prices);
      if (sale) {
        const saving = document.createElement('span');
        saving.className = 'pb-cro-card-shades__saving';
        saving.textContent = `Megtakarítás: ${this._money(sale.saving)}/db`;
        details.appendChild(saving);
      }
      const limit = [variant.stockLeft, variant.maxLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
      const available = variant.available && limit >= (Number(variant.min) || 1);
      const stock = document.createElement('span');
      stock.className = `pb-cro-card-shades__stock${available ? '' : ' pb-cro-card-shades__stock--out'}`;
      stock.textContent = available ? 'Elérhető' : 'Indisponibil';
      details.appendChild(stock);
      const quantityHint = document.createElement('span');
      quantityHint.className = 'pb-cro-card-shades__stock';
      quantityHint.setAttribute('role', 'status');
      quantityHint.setAttribute('aria-live', 'polite');
      quantityHint.id = `pb-qty-limit-${this.dataset.productId}-${id}-${Math.random().toString(36).slice(2, 8)}`;
      details.appendChild(quantityHint);
      const controls = document.createElement('div');
      controls.className = 'pb-cro-card-shades__qty';
      const minus = document.createElement('button');
      minus.type = 'button';
      minus.textContent = '−';
      minus.setAttribute('aria-label', `Mennyiség csökkentése: ${title.textContent}`);
      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.setAttribute('aria-describedby', quantityHint.id);
      input.min = '0';
      input.step = '1';
      input.value = String(this.drafts.get(id) || 0);
      input.setAttribute('aria-label', `Mennyiség: ${title.textContent}`);
      const plus = document.createElement('button');
      plus.type = 'button';
      plus.textContent = '+';
      plus.setAttribute('aria-label', `Mennyiség növelése: ${title.textContent}`);
      const sync = () => {
        const qty = this.drafts.get(id) || 0;
        const left = [variant.stockLeft, variant.maxLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
        const canAdd = variant.available && left >= (Number(variant.min) || 1);
        minus.disabled = this.busy || qty === 0;
        plus.disabled = this.busy || !canAdd || !validQty(nextQty(qty, 1, variant), variant);
        input.disabled = this.busy || (!canAdd && qty === 0);
        input.value = String(qty);
        stock.classList.toggle('pb-cro-card-shades__stock--out', !canAdd);
        stock.textContent = canAdd ? 'Elérhető' : 'Indisponibil';
        const maximum = maximumQty(variant);
        quantityHint.hidden = maximum === null;
        quantityHint.textContent = maximum === null ? '' : `Legfeljebb ${maximum} db-ot adhatsz még a kosárhoz.`;
        if (maximum === null) input.removeAttribute('max');
        else input.max = String(maximum);
      };
      row._syncQty = sync;
      const set = (qty) => {
        if (this.busy) { input.value = String(this.drafts.get(id) || 0); return; }
        const maximum = maximumQty(variant);
        const clamped = Number.isFinite(qty) && maximum !== null && qty > maximum;
        if (clamped) qty = maximum;
        if (qty > 0 && !variant.available || !validQty(qty, variant)) {
          const maximum = maximumQty(variant);
          quantityHint.hidden = false;
          quantityHint.textContent = maximum !== null && qty > maximum ? `Legfeljebb ${maximum} db-ot adhatsz még a kosárhoz.` : 'A megadott mennyiség nem felel meg a termék feltételeinek.';
          this._setMessage(`${title.textContent}: ${quantityHint.textContent}`);
          input.value = String(this.drafts.get(id) || 0);
          return;
        }
        const newlySelected = qty > 0 && !(this.drafts.get(id) > 0);
        if (qty) this.drafts.set(id, qty);
        else this.drafts.delete(id);
        if (newlySelected) window.PBCroAnalytics?.emit('shade_select', { product_id: this.dataset.productId, shade_code: variant.shade || variant.title, location: this.analyticsLocation || (this.isPdp ? 'pdp' : 'card') });
        sync();
        this._setMessage(clamped ? `A mennyiséget ${maximum} db-ra módosítottuk: ennyit adhatsz még a kosárhoz.` : '');
        this._updateSummary();
      };
      minus.addEventListener('click', () => {
        const candidate = nextQty(this.drafts.get(id) || 0, -1, variant);
        set(variant.available && validQty(candidate, variant) ? candidate : 0);
      });
      plus.addEventListener('click', () => set(nextQty(this.drafts.get(id) || 0, 1, variant)));
      input.addEventListener('change', () => set(Number(input.value)));
      controls.append(minus, input, plus);
      if (this.isPdp) {
        const preview = document.createElement('button');
        preview.type = 'button';
        preview.className = 'pb-cro-card-shades__preview';
        preview.setAttribute('aria-label', `Árnyalat megjelenítése: ${title.textContent}`);
        preview.addEventListener('click', () => this._previewVariant(variant));
        row.append(preview);
      }
      row.append(swatch, details, controls);
      sync();
      return row;
    }

    _filter() {
      if (!this.panel) return;
      const query = normalize(this.search.value).trim();
      let visible = 0;
      for (const row of this.rows.values()) {
        row.hidden = !!query && !row.dataset.search.includes(query);
        if (!row.hidden) visible += 1;
      }
      let empty = this.list.querySelector('.pb-cro-card-shades__empty');
      if (visible || !this.loaded) { if (empty) empty.remove(); return; }
      if (!empty) { empty = document.createElement('p'); empty.className = 'pb-cro-card-shades__empty'; this.list.appendChild(empty); }
      empty.textContent = query ? (this.isSizePicker ? 'Nem található változat.' : 'Nem található árnyalat.') : (this.isSizePicker ? 'Nincsenek elérhető változatok.' : 'Nincsenek elérhető árnyalatok.');
    }

    _continueToSet() {
      if (!this.drafts.size) return;
      this.close(false);
      const set = this.closest('.product-info')?.querySelector('pb-cro-set');
      set?.update();
      set?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this._setMessage('Az árnyalatok ki vannak választva. Válaszd ki a további termékeket, majd add hozzá a teljes kiválasztást.');
      return { selectionReady: true };
    }

    async add(extras = new Map(), options = {}) {
      
      if (!this.loaded || !this.drafts.size || this.busy || this.uncertain) return;
      const helper = window.Alpine && Alpine.store && Alpine.store('xCartHelper');
      if (!helper || !window.PBCroCart) { this._setMessage('A kosár most nem érhető el. Próbáld újra.'); return; }
      this.busy = true;
      this._updateSummary();
      const requested = new Map(this.drafts);
      for (const [id, qty] of extras) requested.set(String(id), (requested.get(String(id)) || 0) + qty);
      const result = await window.PBCroCart.add(requested, this.isPdp ? 'product' : 'collection', this.isSetFlow ? { openCart: false } : {});
      if (result.uncertain) {
        this.uncertain = true;
        this._setMessage('Az eredményt nem sikerült ellenőrizni. Előbb nézd meg a kosarat, majd újabb próbálkozás előtt töltsd újra az oldalt.');
      } else {
        this.drafts = new Map([...result.remaining].filter(([id]) => this.variants.has(id)));
        for (const [id, qty] of result.deltas) {
          if (this.isSetFlow) this.committed.set(id, (this.committed.get(id) || 0) + qty);
          const variant = this.variants.get(id);
          if (!variant) continue;
          if (variant.stockLeft !== null) variant.stockLeft = Math.max(0, variant.stockLeft - qty);
          if (variant.maxLeft !== null) variant.maxLeft = Math.max(0, variant.maxLeft - qty);
        }
        for (const row of this.rows.values()) row._syncQty();
        if (result.complete) { this._setMessage((this.isSizePicker ? 'A kiválasztott termékek a kosárba kerültek.' : 'A kiválasztott árnyalatok a kosárba kerültek.')); this.close(false); }
        else if (result.added) { this._setMessage('Néhány termék a kosárba került. Újabb próbálkozás előtt ellenőrizd a többi kiválasztott terméket és a készletet.'); this.close(false); }
        else this._setMessage('Nem kerültek termékek a kosárba. Ellenőrizd a készletet és a mennyiségeket.');
        this.loaded = false;
      }
      this.busy = false;
      this._updateSummary();
      if (this.panel && !this.loadingPromise && !this.uncertain) this._load();
      if (this.isSetFlow && result.added) this._continueToSet();
      return result;
    }
  }

  customElements.define('pb-card-shades', CardShades);
})();

} else {
/* Card and PDP multi-shade order. Variant data is requested from the product's
   section endpoint after opening; each instance owns its own draft quantities. */
(function () {
  'use strict';

  function quantities(cart) {
    const result = new Map();
    for (const item of (cart && cart.items) || []) {
      const id = String(item.variant_id || item.id);
      result.set(id, (result.get(id) || 0) + Number(item.quantity || 0));
    }
    return result;
  }

  function reconcileCart(drafts, before, after) {
    const previous = quantities(before);
    const current = quantities(after);
    const remaining = new Map();
    const deltas = new Map();
    let added = 0;
    let requested = 0;
    for (const [id, qty] of drafts) {
      const wanted = Number(qty);
      const delta = Math.max(0, (current.get(String(id)) || 0) - (previous.get(String(id)) || 0));
      const done = Math.min(wanted, delta);
      requested += wanted;
      added += done;
      if (done) deltas.set(String(id), done);
      if (wanted > done) remaining.set(String(id), wanted - done);
    }
    return { remaining, deltas, added, requested, complete: added === requested };
  }

  function validQty(value, variant) {
    if (!Number.isSafeInteger(value) || value < 0) return false;
    if (value === 0) return true;
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    const limit = [variant.maxLeft, variant.stockLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
    return value >= min && (value - min) % step === 0 && value <= limit;
  }

  function maximumQty(variant) {
    const limit = [variant.maxLeft, variant.stockLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
    if (!Number.isFinite(limit)) return null;
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    return !variant.available || limit < min ? 0 : min + Math.floor((limit - min) / step) * step;
  }

  function nextQty(current, direction, variant) {
    const min = Math.max(1, Number(variant.min) || 1);
    const step = Math.max(1, Number(variant.step) || 1);
    if (direction < 0) return current <= min ? 0 : current - step;
    return current === 0 ? min : current + step;
  }

  function saleFor(variant) {
    const current = Number(variant.price);
    const previous = Number(variant.compareAt);
    if (!Number.isSafeInteger(current) || !Number.isSafeInteger(previous) || current < 0 || previous <= current) return null;
    const percent = Math.round((previous - current) * 100 / previous);
    return { saving: previous - current, percent: percent > 0 ? percent : null };
  }

  function selectionTotals(drafts, variants, loaded, uncertain) {
    let count = 0, pieces = 0, current = 0, reference = 0;
    let valid = !!loaded && !uncertain;
    for (const [id, qty] of drafts) {
      if (qty === 0) continue;
      count += 1;
      pieces += qty;
      const variant = variants.get(String(id));
      const price = Number(variant && variant.price);
      if (!variant || !variant.available || !validQty(qty, variant) || !Number.isSafeInteger(price) || price < 0) { valid = false; continue; }
      const sale = saleFor(variant);
      current += price * qty;
      reference += (sale ? Number(variant.compareAt) : price) * qty;
      if (!Number.isSafeInteger(current) || !Number.isSafeInteger(reference)) valid = false;
    }
    const saving = valid && count ? reference - current : 0;
    const roundedPercent = saving > 0 && reference > 0 ? Math.round(saving * 100 / reference) : 0;
    return { count, pieces, current, reference, saving, percent: roundedPercent > 0 ? `${roundedPercent}%` : saving > 0 ? '<1%' : null, valid };
  }

  function usableSections(body) {
    const sections = body && body.sections;
    return sections && Object.values(sections).length && Object.values(sections).every((html) => typeof html === 'string' && html.length) ? sections : null;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { quantities, reconcileCart, validQty, maximumQty, nextQty, usableSections, saleFor, selectionTotals };
  if (typeof customElements === 'undefined' || customElements.get('pb-card-shades')) return;

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const money = (cents, currencyCodeEnabled = false) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'RON';
    if (currency === 'RON') return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' lei';
    const helper = window.Alpine && Alpine.store && Alpine.store('xHelper');
    const formats = window.Eurus;
    const format = formats && (currencyCodeEnabled ? (formats.money_with_currency_format || formats.money_format) : (formats.money_format || formats.money_with_currency_format));
    if (helper && format) {
      const html = helper.formatMoney(cents, format);
      const span = document.createElement('span');
      span.innerHTML = html;
      return span.textContent.trim();
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'RON' }).format(cents / 100);
  };
  const normalize = (text) => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/(\d)\s*[-/,]\s*(\d)/g, '$1.$2');
  const shadeCollator = new Intl.Collator(document.documentElement.lang || 'ro', { numeric: true, sensitivity: 'base' });
  function compareShades(a, b) {
    const code = (variant) => {
      const label = String(variant.shade || variant.title || '').split('/').pop().trim();
      const match = label.match(/^(\d+)(?:\.(\d+))?(?=\s|$)/);
      if (!match) return null;
      const digits = match[1];
      const intense = digits !== '11' && digits.length > 1 && /^([1-9])\1+$/.test(digits);
      const reflect = match[2] || '';
      return { level: Number(intense ? digits[0] : digits), intensity: intense ? 1 : 0, reflectMain: Number(reflect[0] || 0), reflectSecondary: Number(reflect.slice(1) || 0) };
    };
    const left = code(a), right = code(b);
    if (left && right) return left.level - right.level || left.intensity - right.intensity || left.reflectMain - right.reflectMain || left.reflectSecondary - right.reflectSecondary || shadeCollator.compare(a.shade || a.title, b.shade || b.title);
    if (left) return -1;
    if (right) return 1;
    return shadeCollator.compare(a.shade || a.title, b.shade || b.title);
  }

  class CardShades extends HTMLElement {
    _shadeTitle(variant) {
      const index = Number(this.dataset.shadeOptionIndex ?? -1);
      return variant.shade || (index >= 0 && variant.options?.[index]) || variant.title;
    }

    _money(cents) { return money(cents, this.dataset.currencyCodeEnabled === 'true'); }

    connectedCallback() {
      this.isSizePicker = this.dataset.pickerKind === 'size';
      this.isPdp = this.dataset.mode === 'pdp';
      this.isSetFlow = this.isPdp && !!this.closest('.product-info')?.querySelector('pb-cro-set');
      this.trigger = this.querySelector('.pb-cro-card-shades__trigger');
      this.addButton = this.querySelector('.pb-cro-card-shades__add');
      if (this.isSetFlow) this.addButton.textContent = 'Adaugă nuanțele și continuă';
      this.actions = this.querySelector('.pb-cro-card-shades__actions');
      this.savings = this.querySelector('.pb-cro-card-shades__savings');
      this.message = this.querySelector('.pb-cro-card-shades__message');
      this.variants = new Map();
      this.rows = new Map();
      this.drafts = new Map();
      this.committed = new Map();
      this.loaded = false;
      this.busy = false;
      this.uncertain = false;
      this.refreshQueued = false;
      if (this.isPdp) {
        this.sticky = this.querySelector('.pb-cro-shade-sticky');
        if (this.sticky) document.body.appendChild(this.sticky);
        this.stickyButton = this.sticky?.querySelector('.pb-cro-shade-sticky__add');
        this.stickyButton?.addEventListener('click', () => this.stickyHasSelection ? this.add() : this.open());
        this.sticky?.querySelector('.pb-cro-shade-sticky__edit').addEventListener('click', () => this.open());
        this._onStickyVisibility = (event) => {
          if (event.detail.section !== this.dataset.pdpSection) return;
          this.stickyVisible = event.detail.visible;
          if (this.sticky) this.sticky.hidden = !this.stickyVisible || !!this.panel;
        };
        window.addEventListener('pb-cro-sticky-visibility', this._onStickyVisibility);
        this.pdpRoot = this.closest('[data-pb-cro-pdp-multi]');
        this.technicalForm = this.pdpRoot?.querySelector(`#product-form-${this.dataset.pdpSection}`);
        this._guardTechnicalForm = (event) => { event.preventDefault(); event.stopImmediatePropagation(); };
        this.technicalForm?.addEventListener('submit', this._guardTechnicalForm, true);
        this._onViewedVariant = (event) => this._variantViewed(event);
        document.addEventListener(`eurus:product-page-variant-select:updated:${this.dataset.pdpSection}`, this._onViewedVariant);
      }
      this.trigger.addEventListener('click', () => this.open());
      this.addButton.addEventListener('click', () => this.add());
      this._onCartChanged = () => {
        if (this.busy) return;
        this.loaded = false;
        if (this.loadingPromise) this.refreshQueued = true;
        else if (this.panel) this._load();
        this._updateSummary();
      };
      this._onCartLineMutation = (event) => {
        if (!this.isPdp || event.action !== 'remove' || event.context !== 'cart') return;
        event.promise?.then(({ cart }) => {
          if (!cart) return;
          const removed = new Set((event.lines || []).map(line => String(line.id || line.merchandiseId || '').split(':')[0]));
          let cleared = false;
          for (const id of this.drafts.keys()) {
            if (removed.has(id)) { this.drafts.delete(id); cleared = true; }
          }
          for (const id of this.committed.keys()) {
            if (removed.has(id)) { this.committed.delete(id); cleared = true; }
          }
          if (cleared) {
            for (const row of this.rows.values()) row._syncQty();
            this._updateSummary();
          }
        }).catch(() => {});
      };
      if (this.isPdp) document.addEventListener('shopify:cart:lines-update', this._onCartLineMutation);
      document.addEventListener('eurus:cart:items-changed', this._onCartChanged);
      this._updateSummary();
    }

    disconnectedCallback() {
      this.close(false);
      document.removeEventListener('eurus:cart:items-changed', this._onCartChanged);
      if (this.isPdp) document.removeEventListener('shopify:cart:lines-update', this._onCartLineMutation);
      if (this.isPdp) {
        if (this.sticky) {
          const replacement = this.sticky.cloneNode(true);
          replacement.hidden = true;
          this.appendChild(replacement);
          this.sticky.remove();
        }
        window.removeEventListener('pb-cro-sticky-visibility', this._onStickyVisibility);
        this.technicalForm?.removeEventListener('submit', this._guardTechnicalForm, true);
        document.removeEventListener(`eurus:product-page-variant-select:updated:${this.dataset.pdpSection}`, this._onViewedVariant);
        clearTimeout(this.previewTimeout);
      }
    }

    _highlightViewed(scroll = false) {
      if (!this.isPdp) return;
      for (const [id, row] of this.rows) row.classList.toggle('pb-cro-card-shades__row--viewed', id === this.dataset.viewedVariantId);
      const viewed = this.rows.get(this.dataset.viewedVariantId);
      if (scroll && viewed && !viewed.hidden) viewed.scrollIntoView({ block: 'center' });
    }

    _variantViewed(event) {
      const data = event.detail?.html?.querySelector('.variant-selects-main [data-selected-variant]');
      if (!data) return;
      let variant;
      try { variant = JSON.parse(data.textContent); } catch (_) { return; }
      const id = variant?.id ? String(variant.id) : '';
      this.dataset.viewedVariantId = id;
      const title = this.querySelector('[data-pb-cro-viewed-label]');
      if (title) title.textContent = id ? this._shadeTitle(variant) : 'niciuna';
      const context = this.querySelector('[data-pb-cro-viewed-context]');
      if (context) context.textContent = id
        ? 'Prețul și stocul de mai sus se referă la această variantă. Totalul selecției este separat.'
        : 'Alege nuanțele pentru a vedea prețurile exacte și totalul selecției.';
      this._highlightViewed();
      if (!this.previewPending) return;
      clearTimeout(this.previewTimeout);
      this.previewPending = false;
      this._syncPreviewButtons();
      if (id === this.previewTargetId) {
        this._setMessage(`Nuanța afișată: ${this._shadeTitle(variant)}. Cantitățile alese au rămas neschimbate.`);
        this._highlightViewed();
      } else this._setMessage('Nu am putut afișa nuanța cerută. Selecția pentru coș a rămas neschimbată.');
    }

    _syncPreviewButtons() {
      for (const row of this.rows.values()) {
        const button = row.querySelector('.pb-cro-card-shades__preview');
        if (button) button.disabled = !!this.previewPending;
      }
    }

    _previewVariant(variant) {
      if (!this.isPdp || this.previewPending) return;
      const picker = this.pdpRoot?.querySelector('.variant-selects-main');
      const selects = [...(picker?.querySelectorAll('select.product-form__input') || [])];
      const options = Array.isArray(variant.options) ? variant.options : [];
      const matches = selects.map((select, index) => [...select.options].find((option) => option.value === options[index] && option.dataset.optionValueId));
      // Check the entire combination before touching the technical picker.
      if (!selects.length || selects.length !== options.length || matches.some((option) => !option)) {
        this._setMessage('Această combinație nu poate fi afișată acum. Selecția pentru coș a rămas neschimbată.');
        return;
      }
      const previous = selects.map((select) => select.selectedIndex);
      this.previewPending = true;
      this.previewTargetId = String(variant.id);
      this._syncPreviewButtons();
      this._setMessage('Se afișează nuanța…');
      selects.forEach((select, index) => {
        [...select.options].forEach((option) => option.toggleAttribute('selected', option === matches[index]));
        select.selectedIndex = [...select.options].indexOf(matches[index]);
      });
      selects[selects.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      this.previewTimeout = setTimeout(() => {
        if (!this.previewPending) return;
        this.previewPending = false;
        const current = this.pdpRoot?.querySelector('.variant-selects-main');
        if (current === picker) selects.forEach((select, index) => {
          [...select.options].forEach((option, optionIndex) => { option.selected = optionIndex === previous[index]; option.toggleAttribute('selected', optionIndex === previous[index]); });
        });
        this._syncPreviewButtons();
        this._setMessage('Nu am putut actualiza nuanța afișată. Încearcă din nou. Selecția pentru coș a rămas neschimbată.');
      }, 12000);
    }

    _setMessage(text) {
      this.message.textContent = text || '';
      if (this.panelMessage) this.panelMessage.textContent = text || '';
    }

    _updateSummary() {
      const { count, pieces, current, reference, saving, percent, valid } = selectionTotals(this.drafts, this.variants, this.loaded, this.uncertain);
      const label = this.isSizePicker ? (count ? `${count} ${count === 1 ? "variantă" : "variante"} · ${pieces} ${pieces === 1 ? "bucată" : "bucăți"}` : (this.dataset.pickerLabel || 'Selectează varianta')) : (count ? `${count} nuanțe · ${pieces} bucăți` : 'Selectează nuanțele');
      this.trigger.firstChild.textContent = `${label} `;
      const total = this.actions.querySelector('.pb-cro-card-shades__total');
      total.removeAttribute('aria-label');
      if (!count || !valid) total.textContent = `Total: ${count ? 'se actualizează' : '—'}`;
      else if (saving > 0) {
        const before = document.createElement('s');
        before.className = 'pb-cro-card-shades__reference';
        before.textContent = this._money(reference);
        const now = document.createElement('strong');
        now.className = 'pb-cro-card-shades__current-total';
        now.textContent = this._money(current);
        total.replaceChildren(document.createTextNode('Total: '), before, document.createTextNode(' '), now);
        total.setAttribute('aria-label', `Total înainte de reducere ${before.textContent}. Total de plată ${now.textContent}.`);
      } else total.textContent = `Total: ${this._money(current)}`;
      if (this.savings) {
        this.savings.hidden = !valid || !count || saving <= 0;
        this.savings.textContent = this.savings.hidden ? '' : `Economisești ${this._money(saving)} în total (${percent})`;
      }
      this.addButton.disabled = !valid || !count || this.busy || this.uncertain;
      if (this.sticky) {
        this.stickyHasSelection = count > 0;
        this.sticky.querySelector('[data-pb-sticky-total]').hidden = !count;
        this.sticky.querySelector('[data-pb-sticky-total]').replaceChildren(...Array.from(total.childNodes, node => node.cloneNode(true)));
        this.stickyButton.textContent = count ? (this.isSetFlow ? 'Adaugă nuanțele și continuă' : 'Adaugă în coș') : 'Selectează nuanțele';
        this.stickyButton.disabled = this.busy || (count > 0 && this.addButton.disabled);
        this.sticky.hidden = !this.stickyVisible || !!this.panel;
      }
      for (const row of this.rows.values()) row._syncQty();
      if (this.isPdp) this.dispatchEvent(new CustomEvent('pb-cro:selection', { bubbles: true }));
    }

    _moveActions() {
      if (!this.panel) return;
      if (this.actions.parentElement !== this.footer) this.footer.appendChild(this.actions);
    }

    _position() {
      if (!this.panel) return;
      if (window.matchMedia('(max-width: 639px)').matches) {
        // iOS keyboards resize the visual viewport, not necessarily the layout viewport.
        const viewport = window.visualViewport;
        const width = viewport?.width || window.innerWidth;
        const availableHeight = viewport?.height || window.innerHeight;
        const height = Math.min(760, window.innerHeight * .86, availableHeight);
        this.panel.style.setProperty('--pb-panel-left', `${viewport?.offsetLeft || 0}px`);
        this.panel.style.setProperty('--pb-panel-top', `${(viewport?.offsetTop || 0) + availableHeight - height}px`);
        this.panel.style.setProperty('--pb-panel-width', `${width}px`);
        this.panel.style.setProperty('--pb-panel-height', `${height}px`);
        return;
      }
      this.panel.style.removeProperty('--pb-panel-width');
      this.panel.style.removeProperty('--pb-panel-height');
      const rect = this.trigger.getBoundingClientRect();
      const width = Math.min(460, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const height = Math.min(640, window.innerHeight - 24);
      const top = Math.max(12, Math.min(rect.bottom + 6, window.innerHeight - height - 12));
      this.panel.style.setProperty('--pb-panel-left', `${left}px`);
      this.panel.style.setProperty('--pb-panel-top', `${top}px`);
    }

    open() {
      if (this.panel) return;
      document.dispatchEvent(new CustomEvent('pb-cro:card-shades:open', { detail: this }));
      this.analyticsLocation = this.isPdp ? (this.sticky?.contains(document.activeElement) ? 'sticky' : 'pdp') : 'card';
      this.returnFocus = this.isPdp && !this.sticky?.contains(document.activeElement) ? this.trigger : document.activeElement;
      this.overlay = document.createElement('div');
      this.overlay.className = 'pb-cro-card-shades__overlay';
      this.overlay.addEventListener('click', () => this.close());
      this.panel = document.createElement('div');
      this.panel.className = 'pb-cro-card-shades__panel';
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.tabIndex = -1;
      this.panel.setAttribute('aria-label', `Selectează nuanțele pentru ${this.dataset.productTitle || 'produs'}`);
      this.panel.innerHTML = '<div class="pb-cro-card-shades__head"><strong></strong><button type="button" class="pb-cro-card-shades__close" aria-label="Închide">×</button></div><input class="pb-cro-card-shades__search" type="search" placeholder="Caută cod sau nume" aria-label="Caută cod sau nume"><p class="pb-cro-card-shades__load" role="status"></p><div class="pb-cro-card-shades__list"></div><div class="pb-cro-card-shades__footer"><p class="pb-cro-card-shades__footer-note" role="status"></p></div>';
      this.panel.querySelector('.pb-cro-card-shades__head strong').textContent = this.dataset.productTitle || 'Nuanțe';
      this.panel.querySelector('.pb-cro-card-shades__close').addEventListener('click', () => this.close());
      this.search = this.panel.querySelector('.pb-cro-card-shades__search');
      if (this.isSizePicker) {
        const hu = document.documentElement.lang.split('-')[0] === 'hu';
        const label = this.dataset.pickerLabel || (hu ? 'Válassz változatot' : 'Selectează varianta');
        this.panel.setAttribute('aria-label', label + ': ' + this.dataset.productTitle);
        this.search.placeholder = this.dataset.pickerSearch || (hu ? 'Változat keresése' : 'Caută varianta');
        this.search.setAttribute('aria-label', this.search.placeholder);
      }
      this.search.addEventListener('input', () => this._filter());
      this.list = this.panel.querySelector('.pb-cro-card-shades__list');
      this.loadStatus = this.panel.querySelector('.pb-cro-card-shades__load');
      this.footer = this.panel.querySelector('.pb-cro-card-shades__footer');
      this.panelMessage = this.panel.querySelector('.pb-cro-card-shades__footer-note');
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(); }
        if (event.key !== 'Tab') return;
        const targets = [
          this.panel.querySelector('.pb-cro-card-shades__close'),
          this.search,
          ...this.list.querySelectorAll('button,input'),
          this.addButton
        ].filter((el) => el && !el.disabled && !el.closest('[hidden]'));
        if (!targets.length) return;
        if (event.shiftKey && document.activeElement === targets[0]) { event.preventDefault(); targets[targets.length - 1].focus(); }
        else if (!event.shiftKey && document.activeElement === targets[targets.length - 1]) { event.preventDefault(); targets[0].focus(); }
      });
      document.body.append(this.overlay, this.panel);
      this.trigger.setAttribute('aria-expanded', 'true');
      this._moveActions();
      this._position();
      this._onResize = () => { this._moveActions(); this._position(); };
      window.addEventListener('resize', this._onResize);
      window.visualViewport?.addEventListener('resize', this._onResize);
      window.visualViewport?.addEventListener('scroll', this._onResize);
      this._onOtherOpen = (event) => { if (event.detail !== this) this.close(false); };
      document.addEventListener('pb-cro:card-shades:open', this._onOtherOpen);
      this._updateSummary();
      // Opening the mobile picker must not open the keyboard or trigger Safari zoom.
      (window.matchMedia('(max-width: 639px)').matches ? this.panel : this.search).focus({ preventScroll: true });
      if (!this.loaded && !this.loadingPromise) this._load();
      else { this._renderAll(); this._highlightViewed(true); }
    }

    close(restoreFocus = true) {
      if (!this.panel) return;
      this.insertBefore(this.actions, this.message);
      this.overlay.remove();
      this.panel.remove();
      this.panel = null;
      this.rows.clear();
      this.panelMessage = null;
      this.trigger.setAttribute('aria-expanded', 'false');
      window.removeEventListener('resize', this._onResize);
      window.visualViewport?.removeEventListener('resize', this._onResize);
      window.visualViewport?.removeEventListener('scroll', this._onResize);
      document.removeEventListener('pb-cro:card-shades:open', this._onOtherOpen);
      this._updateSummary();
      if (restoreFocus && this.isConnected) (this.returnFocus || this.trigger).focus({ preventScroll: true });
    }

    async _load() {
      this.loaded = false;
      this.variants.clear();
      this.rows.clear();
      this.list.replaceChildren();
      this.loadStatus.textContent = (this.isSizePicker ? 'Se încarcă variantele…' : 'Se încarcă nuanțele…');
      this.loadingPromise = (async () => {
        let next = this.dataset.productUrl;
        const seen = new Set();
        while (next) {
          const url = new URL(next, location.origin);
          url.searchParams.set('section_id', 'pb-cro-card-shades');
          const preview = new URLSearchParams(location.search).get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          if (seen.has(url.href)) throw new Error('Repeated variant page');
          seen.add(url.href);
          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Variant page unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = html.querySelector('[data-pb-card-page]');
          const data = page && page.querySelector('[data-pb-card-variants]');
          if (!data) throw new Error('Variant data missing');
          const variants = JSON.parse(data.textContent);
          for (const variant of variants) this.variants.set(String(variant.id), variant);
          if (this.panel) { this._renderAll(); this.loadStatus.textContent = (this.isSizePicker ? 'Se încarcă toate variantele…' : 'Se încarcă toate nuanțele…'); }
          next = page.dataset.nextUrl || '';
        }
        this.loaded = true;
        if (this.panel) { this.loadStatus.textContent = ''; this._renderAll(); this._highlightViewed(true); }
      })().catch(() => {
        this.loaded = false;
        if (this.panel) this.loadStatus.textContent = (this.isSizePicker ? 'Nu am putut încărca variantele. Închide și încearcă din nou.' : 'Nu am putut încărca nuanțele. Închide și încearcă din nou.');
      }).finally(() => {
        this.loadingPromise = null;
        this._updateSummary();
        if (this.refreshQueued) {
          this.refreshQueued = false;
          this.loaded = false;
          if (this.panel) this._load();
        }
      });
      return this.loadingPromise;
    }

    _renderAll() {
      if (!this.panel) return;
      const orderedVariants = [...this.variants];
      if (!this.isSizePicker) orderedVariants.sort((a, b) => compareShades(a[1], b[1]));
      for (const [id, variant] of orderedVariants) {
        if (!this.rows.has(id)) {
          const row = this._row(variant);
          this.rows.set(id, row);
          this.list.appendChild(row);
        }
      }
      this._filter();
      this._highlightViewed();
      this._updateSummary();
    }

    _row(variant) {
      const id = String(variant.id);
      const row = document.createElement('div');
      row.className = 'pb-cro-card-shades__row';
      row.dataset.search = normalize([variant.sku, variant.shade, variant.title, ...(variant.options || [])].join(' '));
      const swatch = document.createElement('span');
      swatch.className = 'pb-cro-card-shades__swatch';
      if (variant.swatchImage) {
        const img = document.createElement('img');
        img.src = variant.swatchImage;
        img.alt = '';
        img.width = 36;
        img.height = 36;
        img.loading = 'lazy';
        swatch.appendChild(img);
      } else if (/^\d{1,3},\s*\d{1,3},\s*\d{1,3}$/.test(variant.swatchRgb || '')) {
        swatch.style.backgroundColor = `rgb(${variant.swatchRgb})`;
      } else swatch.classList.add('pb-cro-card-shades__swatch--empty');
      const details = document.createElement('div');
      details.className = 'pb-cro-card-shades__details';
      const title = document.createElement('span');
      title.className = 'pb-cro-card-shades__name';
      title.textContent = this._shadeTitle(variant);
      details.appendChild(title);
      if (variant.sku) {
        const code = document.createElement('span');
        code.className = 'pb-cro-card-shades__sku';
        code.textContent = `Cod: ${variant.sku}`;
        details.appendChild(code);
      }
      const prices = document.createElement('span');
      prices.className = 'pb-cro-card-shades__prices';
      const sale = saleFor(variant);
      if (sale) {
        const compare = document.createElement('s');
        compare.className = 'pb-cro-card-shades__compare';
        compare.textContent = variant.compareAtText || this._money(variant.compareAt);
        prices.appendChild(compare);
      }
      const price = document.createElement('span');
      price.className = sale ? 'pb-cro-card-shades__current--sale' : '';
      price.textContent = variant.priceText || this._money(variant.price);
      prices.appendChild(price);
      if (sale && sale.percent) {
        const percent = document.createElement('span');
        percent.className = 'pb-cro-card-shades__discount';
        percent.textContent = `−${sale.percent}%`;
        prices.appendChild(percent);
      }
      details.appendChild(prices);
      if (sale) {
        const saving = document.createElement('span');
        saving.className = 'pb-cro-card-shades__saving';
        saving.textContent = `Economisești ${this._money(sale.saving)}/buc.`;
        details.appendChild(saving);
      }
      const limit = [variant.stockLeft, variant.maxLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
      const available = variant.available && limit >= (Number(variant.min) || 1);
      const stock = document.createElement('span');
      stock.className = `pb-cro-card-shades__stock${available ? '' : ' pb-cro-card-shades__stock--out'}`;
      stock.textContent = available ? 'Disponibil' : 'Indisponibil';
      details.appendChild(stock);
      const quantityHint = document.createElement('span');
      quantityHint.className = 'pb-cro-card-shades__stock';
      quantityHint.setAttribute('role', 'status');
      quantityHint.setAttribute('aria-live', 'polite');
      quantityHint.id = `pb-qty-limit-${this.dataset.productId}-${id}-${Math.random().toString(36).slice(2, 8)}`;
      details.appendChild(quantityHint);
      const controls = document.createElement('div');
      controls.className = 'pb-cro-card-shades__qty';
      const minus = document.createElement('button');
      minus.type = 'button';
      minus.textContent = '−';
      minus.setAttribute('aria-label', `Scade cantitatea pentru ${title.textContent}`);
      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.setAttribute('aria-describedby', quantityHint.id);
      input.min = '0';
      input.step = '1';
      input.value = String(this.drafts.get(id) || 0);
      input.setAttribute('aria-label', `Cantitate pentru ${title.textContent}`);
      const plus = document.createElement('button');
      plus.type = 'button';
      plus.textContent = '+';
      plus.setAttribute('aria-label', `Crește cantitatea pentru ${title.textContent}`);
      const sync = () => {
        const qty = this.drafts.get(id) || 0;
        const left = [variant.stockLeft, variant.maxLeft].filter((n) => n !== null && n !== undefined).reduce((a, b) => Math.min(a, Number(b)), Infinity);
        const canAdd = variant.available && left >= (Number(variant.min) || 1);
        minus.disabled = this.busy || qty === 0;
        plus.disabled = this.busy || !canAdd || !validQty(nextQty(qty, 1, variant), variant);
        input.disabled = this.busy || (!canAdd && qty === 0);
        input.value = String(qty);
        stock.classList.toggle('pb-cro-card-shades__stock--out', !canAdd);
        stock.textContent = canAdd ? 'Disponibil' : 'Indisponibil';
        const maximum = maximumQty(variant);
        quantityHint.hidden = maximum === null;
        quantityHint.textContent = maximum === null ? '' : `Poți adăuga maximum ${maximum} ${maximum === 1 ? "bucată" : "bucăți"}.`;
        if (maximum === null) input.removeAttribute('max');
        else input.max = String(maximum);
      };
      row._syncQty = sync;
      const set = (qty) => {
        if (this.busy) { input.value = String(this.drafts.get(id) || 0); return; }
        const maximum = maximumQty(variant);
        const clamped = Number.isFinite(qty) && maximum !== null && qty > maximum;
        if (clamped) qty = maximum;
        if (qty > 0 && !variant.available || !validQty(qty, variant)) {
          const maximum = maximumQty(variant);
          quantityHint.hidden = false;
          quantityHint.textContent = maximum !== null && qty > maximum ? `Poți adăuga maximum ${maximum} ${maximum === 1 ? "bucată" : "bucăți"}.` : 'Cantitatea nu respectă regulile produsului.';
          this._setMessage(`${title.textContent}: ${quantityHint.textContent}`);
          input.value = String(this.drafts.get(id) || 0);
          return;
        }
        const newlySelected = qty > 0 && !(this.drafts.get(id) > 0);
        if (qty) this.drafts.set(id, qty);
        else this.drafts.delete(id);
        if (newlySelected) window.PBCroAnalytics?.emit('shade_select', { product_id: this.dataset.productId, shade_code: variant.shade || variant.title, location: this.analyticsLocation || (this.isPdp ? 'pdp' : 'card') });
        sync();
        this._setMessage(clamped ? `Cantitatea a fost ajustată la ${maximum}: acesta este maximul pe care îl poți adăuga.` : '');
        this._updateSummary();
      };
      minus.addEventListener('click', () => {
        const candidate = nextQty(this.drafts.get(id) || 0, -1, variant);
        set(variant.available && validQty(candidate, variant) ? candidate : 0);
      });
      plus.addEventListener('click', () => set(nextQty(this.drafts.get(id) || 0, 1, variant)));
      input.addEventListener('change', () => set(Number(input.value)));
      controls.append(minus, input, plus);
      if (this.isPdp) {
        const preview = document.createElement('button');
        preview.type = 'button';
        preview.className = 'pb-cro-card-shades__preview';
        preview.setAttribute('aria-label', `Afișează nuanța ${title.textContent}`);
        preview.addEventListener('click', () => this._previewVariant(variant));
        row.append(preview);
      }
      row.append(swatch, details, controls);
      sync();
      return row;
    }

    _filter() {
      if (!this.panel) return;
      const query = normalize(this.search.value).trim();
      let visible = 0;
      for (const row of this.rows.values()) {
        row.hidden = !!query && !row.dataset.search.includes(query);
        if (!row.hidden) visible += 1;
      }
      let empty = this.list.querySelector('.pb-cro-card-shades__empty');
      if (visible || !this.loaded) { if (empty) empty.remove(); return; }
      if (!empty) { empty = document.createElement('p'); empty.className = 'pb-cro-card-shades__empty'; this.list.appendChild(empty); }
      empty.textContent = query ? (this.isSizePicker ? 'Nicio variantă găsită.' : 'Nicio nuanță găsită.') : (this.isSizePicker ? 'Nu sunt variante disponibile.' : 'Nu sunt nuanțe disponibile.');
    }

    _continueToSet() {
      if (!this.drafts.size) return;
      this.close(false);
      const set = this.closest('.product-info')?.querySelector('pb-cro-set');
      set?.update();
      set?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this._setMessage('Nuanțele sunt în coș. Alege produse opționale sau mergi la finalizarea comenzii.');
      return { selectionReady: true };
    }

    async add(extras = new Map(), options = {}) {
      
      if (!this.loaded || !this.drafts.size || this.busy || this.uncertain) return;
      const helper = window.Alpine && Alpine.store && Alpine.store('xCartHelper');
      if (!helper || !window.PBCroCart) { this._setMessage('Coșul nu este disponibil acum. Încearcă din nou.'); return; }
      this.busy = true;
      this._updateSummary();
      const requested = new Map(this.drafts);
      for (const [id, qty] of extras) requested.set(String(id), (requested.get(String(id)) || 0) + qty);
      const result = await window.PBCroCart.add(requested, this.isPdp ? 'product' : 'collection', this.isSetFlow ? { openCart: false } : {});
      if (result.uncertain) {
        this.uncertain = true;
        this._setMessage('Nu am putut verifica rezultatul. Verifică mai întâi coșul; reîncarcă pagina înainte de o nouă încercare.');
      } else {
        this.drafts = new Map([...result.remaining].filter(([id]) => this.variants.has(id)));
        for (const [id, qty] of result.deltas) {
          if (this.isSetFlow) this.committed.set(id, (this.committed.get(id) || 0) + qty);
          const variant = this.variants.get(id);
          if (!variant) continue;
          if (variant.stockLeft !== null) variant.stockLeft = Math.max(0, variant.stockLeft - qty);
          if (variant.maxLeft !== null) variant.maxLeft = Math.max(0, variant.maxLeft - qty);
        }
        for (const row of this.rows.values()) row._syncQty();
        if (result.complete) { this._setMessage((this.isSizePicker ? 'Produsele selectate au fost adăugate în coș.' : 'Nuanțele selectate au fost adăugate în coș.')); this.close(false); }
        else if (result.added) { this._setMessage('Unele bucăți au fost adăugate. Verifică restul selecției și stocul înainte de a încerca din nou.'); this.close(false); }
        else this._setMessage('Nu s-au adăugat produse. Verifică stocul și cantitățile selectate.');
        this.loaded = false;
      }
      this.busy = false;
      this._updateSummary();
      if (this.panel && !this.loadingPromise && !this.uncertain) this._load();
      if (this.isSetFlow && result.added) this._continueToSet();
      return result;
    }
  }

  customElements.define('pb-card-shades', CardShades);
})();

}
